import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import {
  Alert,
  Button,
  Modal,
  Select,
  SpaceBetween,
  Spinner,
  TextContent,
} from '@amzn/awsui-components-react-v3';
import { useMutation } from '@apollo/client';
import { logger } from '@amzn/awsui-components-react/polaris/internal/core/runtime/src';
import { NonCancelableEventHandler } from '@amzn/awsui-components-react-v3/polaris/internal/events';
import { ContainerBlock, ContainerRule } from '../types';
import { CREATE_POLICY, UPDATE_CONTAINER } from '../../../services/container-management/queries';
import { UpdateContainerRequest } from '../../../services/container-management/types';
import {
  getContainerArn,
  getContainerBlock,
  transformContainerBlockToPolicyAction,
} from './helper';

export type ContainerUpdateConfirmationModalProps = {
  containerId: string;
  fqContainerName: string;
  version: number;
  newSection: string;
  newAisle: string;
  newLevel: string;
  newCell: string;
  newAlphabetIdentifier: string;
  newSize: string;
  newState: string;
  newRules: Map<ContainerBlock, ContainerRule>;
  newContainerDefinitionId: string;
  isCategoryUpdated: boolean;
  isSubcategoryUpdated: boolean;
  isSectionUpdated: boolean;
  isAisleUpdated: boolean;
  isLevelUpdated: boolean;
  isCellUpdated: boolean;
  isAlphabetIdentifierUpdated: boolean;
  isSizeUpdated: boolean;
  isStateUpdated: boolean;
  areRulesUpdated: boolean;
  originalRules: Map<ContainerBlock, ContainerRule>;
  sizeReasonCodes: OptionDefinition[];
  rulesReasonCodes: OptionDefinition[];
  onClose: NonCancelableEventHandler;
};

export const ContainerUpdateConfirmationModal = (
  data: ContainerUpdateConfirmationModalProps
): ReactElement => {
  const { t } = useTranslation();
  const [sizeReasonCode, setSizeReasonCode] = useState<OptionDefinition | null>(null);
  const [rulesReasonCode, setRulesReasonCode] = useState<OptionDefinition | null>(null);
  const [updateResponse, setUpdateResponse] = useState(null);

  const [updateContainer, { loading: updateContainerLoading, error: updateContainerError }] =
    useMutation(UPDATE_CONTAINER, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      onCompleted: (result) => {
        setUpdateResponse(result.updateContainer);
      },
    });

  const [createPolicy, { loading: createPolicyLoading, error: createPolicyError }] = useMutation(
    CREATE_POLICY,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
    }
  );

  const callUpdateContainer = (request: UpdateContainerRequest): void => {
    updateContainer({
      variables: {
        ...request,
      },
    }).catch((error) => {
      logger.warn(`Error when updating container: ${error}`);
    });
  };

  const isContainerNameUpdated = () =>
    data.isSectionUpdated ||
    data.isAisleUpdated ||
    data.isLevelUpdated ||
    data.isCellUpdated ||
    data.isAlphabetIdentifierUpdated;

  const handleUpdateContainer = async () => {
    const reasonCodes = [];

    if (sizeReasonCode?.value) {
      reasonCodes.push(parseInt(sizeReasonCode.value, 10));
    }
    if (rulesReasonCode?.value) {
      reasonCodes.push(parseInt(rulesReasonCode.value, 10));
    }

    // TODO what if isActive is the only container change? ReasonCodes is necessary for updateContainer
    // for now, the workaround will be to add a reasonCode for changing isActive (using 0)
    if (data.isStateUpdated) {
      reasonCodes.push(0);
    }

    // there is only one name reason code, which is 400, "name correction change"
    if (isContainerNameUpdated()) {
      reasonCodes.push(400);
    }

    // there is only one reason code for category/subcategory, which is 401, "migration change code"
    if (data.isCategoryUpdated || data.isSubcategoryUpdated) {
      reasonCodes.push(401);
    }

    const request: UpdateContainerRequest = {
      containerId: data.containerId,
      version: data.version,
      reasonCodes,
    };
    if (data.isSectionUpdated) {
      request.metadataStruct = {
        ...request.metadataStruct,
        section: data.newSection,
      };
    }
    if (data.isAisleUpdated) {
      request.metadataStruct = {
        ...request.metadataStruct,
        aisle: data.newAisle,
      };
    }
    if (data.isLevelUpdated) {
      request.metadataStruct = {
        ...request.metadataStruct,
        level: data.newLevel,
      };
    }
    if (data.isAlphabetIdentifierUpdated) {
      request.metadataStruct = {
        ...request.metadataStruct,
        alphabetIdentifier: data.newAlphabetIdentifier,
      };
    }
    if (data.isCellUpdated) {
      request.containerName = data.newCell;
    }
    if (data.isStateUpdated) {
      request.state = data.newState.toUpperCase();
    }
    if (data.isSizeUpdated) {
      request.size = data.newSize;
    }
    if (data.isCategoryUpdated || data.isSubcategoryUpdated) {
      request.containerDefinitionId = data.newContainerDefinitionId;
    }

    if (data.areRulesUpdated) {
      const addedPolicyIds: string[] = [];
      const removedPolicyIds: string[] = [];
      await Promise.all(
        Array.from(data.newRules.keys()).map(async (ruleName) => {
          const block = getContainerBlock(ruleName);
          if (block) {
            const originalRule = data.originalRules.get(block);
            const newRule = data.newRules.get(block);
            if (originalRule && newRule && originalRule.enabled !== newRule.enabled) {
              if (newRule.isOverride) {
                // create a new override policy
                await createPolicy({
                  variables: {
                    statements: [
                      {
                        Effect: newRule.enabled ? 'Deny' : 'Allow',
                        Action: transformContainerBlockToPolicyAction(newRule.rule),
                        Resource: getContainerArn(data.containerId),
                      },
                    ],
                  },
                }).then((result) => {
                  if (result.data?.createPolicy?.policy?.policyId) {
                    addedPolicyIds.push(result.data?.createPolicy?.policy?.policyId);
                  }
                });
              } else if (originalRule.policyId) {
                // Remove the policy ids (which is an override)
                removedPolicyIds.push(originalRule.policyId);
              }
            }
          }
        })
      )
        .catch((error) => {
          logger.warn(`Error when updating policies: ${error}`);
        })
        .then(() => {
          if (addedPolicyIds.length > 0) {
            request.addedPolicyIds = addedPolicyIds;
          }
          if (removedPolicyIds.length > 0) {
            request.removedPolicyIds = removedPolicyIds;
          }
          callUpdateContainer(request);
        });
    } else {
      callUpdateContainer(request);
    }
  };

  if (updateContainerLoading || createPolicyLoading) {
    return (
      <Modal visible header={t('Update Container?')} onDismiss={data.onClose}>
        <Spinner size="big" />
      </Modal>
    );
  }
  if (updateContainerError) {
    return (
      <Modal visible header={t('Update Container?')} onDismiss={data.onClose}>
        <Alert data-test-id="errorAlert" type="error">
          {t(`Container ${data.containerId} update failed due to below error:`)}
          {t(`${updateContainerError}`)}
        </Alert>
      </Modal>
    );
  }
  if (createPolicyError) {
    return (
      <Modal visible header={t('Update Container?')} onDismiss={data.onClose}>
        <Alert data-test-id="errorAlert" type="error">
          {t(`Container ${data.containerId} update failed`)}
        </Alert>
      </Modal>
    );
  }

  if (updateResponse) {
    return (
      <Modal visible header={t('Update Container?')} onDismiss={() => window.location.reload()}>
        <Alert data-test-id="successAlert" type="success">
          {t(`Container ${data.fqContainerName} updated successfully`)}
        </Alert>
      </Modal>
    );
  }

  return (
    <Modal
      visible
      header={t('Update Container?')}
      onDismiss={data.onClose}
      footer={
        <span>
          <Button
            data-test-id="save"
            variant="primary"
            disabled={
              data.newState.toLowerCase() === 'active' &&
              ((data.areRulesUpdated && !rulesReasonCode) ||
                (data.isSizeUpdated && !sizeReasonCode))
            }
            onClick={async () => handleUpdateContainer()}
          >
            {t('Update Container')}
          </Button>
        </span>
      }
    >
      <SpaceBetween direction="vertical" size="m">
        {data.isStateUpdated && (
          <Alert type="warning">
            {t(`You are changing container state to ${data.newState.toUpperCase()}`)}
          </Alert>
        )}
        {data.newState.toLowerCase() === 'active' && isContainerNameUpdated() && (
          <Alert type="warning">{t('You are overriding the name of this container')}</Alert>
        )}
        {(data.isCategoryUpdated || data.isSubcategoryUpdated) && (
          <Alert type="warning">
            {t('You are overriding the Category/Subcategory of this container')}
          </Alert>
        )}
        {data.areRulesUpdated && data.newState.toLowerCase() === 'active' && (
          <>
            <Alert type="warning">{t('You are overriding the rules of this container')}</Alert>
            <TextContent>
              <p>{t('Rule override reason')}</p>
              <p>
                <small>{t("You must provide a reason to override this container's rules")}</small>
              </p>
            </TextContent>
            <Select
              data-test-id="rulesReasonCodeDropdown"
              selectedOption={rulesReasonCode}
              onChange={({ detail }) => {
                setRulesReasonCode(detail.selectedOption);
              }}
              options={data.rulesReasonCodes}
            />
          </>
        )}
        {data.isSizeUpdated && data.newState.toLowerCase() === 'active' && (
          <>
            <TextContent>
              <p>{t('Size change reason')}</p>
              <p>
                <small>{t("You must provide a reason to change this container's size")}</small>
              </p>
            </TextContent>
            <Select
              data-test-id="sizeReasonCodeDropdown"
              selectedOption={sizeReasonCode}
              onChange={({ detail }) => {
                setSizeReasonCode(detail.selectedOption);
              }}
              expandToViewport
              options={data.sizeReasonCodes}
            />
          </>
        )}
      </SpaceBetween>
    </Modal>
  );
};
